import { z } from "zod";

import { estDatePure } from "./jour.js";
import { motsNormalises } from "./content.js";

/**
 * SMART NOTES : les NOTES de l'organisation, reprises d'AFFiNE.
 *
 * Une note a DEUX niveaux, et c'est tout le contrat :
 *   - `orgs/{orgId}/notes/{noteId}` : la FICHE de la note, ses metadonnees
 *     seules (titre, projet, etiquettes, journal, favoris, extrait, liens).
 *     Une liste ne telecharge que des fiches. Le client la LIT ; il ne l'ecrit
 *     jamais : tout geste passe par la callable `notesApi`
 *     (`functions/src/notes/`), la meme que le MCP et Lia ;
 *   - le CONTENU : un document Yjs au format BlockSuite (l'editeur d'AFFiNE),
 *     range par la brique collaborative du socle (`collab.ts`) sous la fiche :
 *     `maj/{id}` (mises a jour incrementales, ecrites par l'editeur en temps
 *     reel), `etat/courant` (l'instantane compacte, ecrit par le serveur) et
 *     `presence/{session}` (qui est la, ou est son curseur).
 *
 * DEUX PROJETS, comme Flux : `org` (Org wide : tout membre lit ; owner, admin
 * et auteur modifient) et `perso` (l'auteur seul lit et modifie). Les rules
 * l'imposent (`firebase/firestore.rules`, bloc `notes`) ; d'ou DEUX requetes
 * fusionnees cote client (`portee == 'org'`, `auteurUid == moi`).
 *
 * LES ETATS DU SOCLE : la CORBEILLE est l'archive (`archivedAt`), restaurable ;
 * « Supprimer definitivement » pose `deletedAt` et le serveur purge le
 * contenu (mises a jour, instantane, pieces jointes). Une note supprimee ne
 * se lit plus nulle part.
 *
 * Les etiquettes (`notesTags`) et les collections (`notesCollections`) vivent
 * a cote, sous `orgs/{orgId}/`. Les favoris sont PERSONNELS : `favorisDe`
 * porte les uid de ceux qui ont mis la note en favori (le serveur n'y touche
 * que l'uid de l'appelant).
 */

export const NOTES_SCHEMA_VERSION = 1 as const;

/** L'id de l'app, celui du registre et des liens profonds. */
export const NOTES_APP_ID = "smart-notes" as const;

/** Les deux PROJETS du rail. */
export const NOTES_PORTEES = ["org", "perso"] as const;
export type NotePortee = (typeof NOTES_PORTEES)[number];

/** Les deux modes de l'editeur d'AFFiNE : la page, et le tableau blanc (edgeless). */
export const NOTES_MODES = ["page", "edgeless"] as const;
export type NoteMode = (typeof NOTES_MODES)[number];

/** Plafonds (ecrits aussi dans les rules quand elles les lisent). */
export const NOTE_TITRE_MAX = 200;
export const NOTE_EXTRAIT_MAX = 280;
export const NOTE_TAGS_MAX = 30;
export const NOTE_LIENS_MAX = 200;
/** Les mentions de membres d'une note (`@[personId]`, une entree par pastille), au plus. */
export const NOTE_MENTIONS_MAX = 100;
export const NOTE_RECHERCHE_MAX = 400;
export const NOTE_RECHERCHE_PREFIXE_MAX = 15;
export const NOTE_TAG_NOM_MAX = 40;
export const NOTE_COLLECTION_NOM_MAX = 80;
export const NOTE_COLLECTION_REGLES_MAX = 10;
export const NOTE_COLLECTION_SELECTION_MAX = 500;
/** Le Markdown accepte par le MCP et l'import, en caracteres. */
export const NOTE_MARKDOWN_MAX = 200_000;

/** Les couleurs d'etiquette, celles de la palette d'AFFiNE (noms, pas de hex : l'ecran les traduit en tokens). */
export const NOTES_COULEURS_TAG = ["gris", "rouge", "orange", "jaune", "vert", "bleu", "violet", "rose"] as const;
export type NoteCouleurTag = (typeof NOTES_COULEURS_TAG)[number];

export interface Note {
  id: string;
  orgId: string;
  schemaVersion: 1;
  titre: string;
  portee: NotePortee;
  /** Le mode d'ouverture par defaut (le dernier choisi). */
  mode: NoteMode;
  auteurUid: string;
  auteurEmail: string;
  /** Ids d'etiquettes (`notesTags`). */
  tags: string[];
  /** La date du journal (`YYYY-MM-DD`), ou `null` pour une note ordinaire. */
  journal: string | null;
  /** Les uid qui l'ont mise en favori. */
  favorisDe: string[];
  /** Le debut du texte, pour la liste et la recherche. */
  extrait: string;
  /** Les debuts de mots du titre et du texte (`prefixesDeNote`), pour `array-contains`. */
  recherche: string[];
  /** Les ids des notes citees (mentions, liens) : les liens retour se calculent dessus. */
  liens: string[];
  /** La derniere modification du CONTENU (l'editeur ou le MCP). */
  modifieeAt: Date;
  modifieePar: string;
  archivedAt: Date | null;
  deletedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

export interface NoteTag {
  id: string;
  orgId: string;
  nom: string;
  couleur: NoteCouleurTag;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

/* -------------------------------------------------------------------------- */
/* Collections : par regles ou par selection (comme AFFiNE)                   */
/* -------------------------------------------------------------------------- */

export const NOTES_CHAMPS_REGLE = ["tag", "titre", "favori", "journal", "portee", "creee", "modifiee"] as const;
export type NoteChampRegle = (typeof NOTES_CHAMPS_REGLE)[number];

export const NoteRegleSchema = z.discriminatedUnion("champ", [
  z.object({ champ: z.literal("tag"), op: z.enum(["contient", "ne_contient_pas"]), valeur: z.string().min(1) }).strict(),
  z.object({ champ: z.literal("titre"), op: z.enum(["contient", "ne_contient_pas"]), valeur: z.string().min(1).max(NOTE_TITRE_MAX) }).strict(),
  z.object({ champ: z.literal("favori"), op: z.literal("est"), valeur: z.boolean() }).strict(),
  z.object({ champ: z.literal("journal"), op: z.literal("est"), valeur: z.boolean() }).strict(),
  z.object({ champ: z.literal("portee"), op: z.literal("est"), valeur: z.enum(NOTES_PORTEES) }).strict(),
  z.object({ champ: z.enum(["creee", "modifiee"]), op: z.literal("derniers_jours"), valeur: z.number().int().min(1).max(3650) }).strict(),
]);
export type NoteRegle = z.infer<typeof NoteRegleSchema>;

export const NoteCollectionSaisieSchema = z
  .object({
    nom: z.string().trim().min(1).max(NOTE_COLLECTION_NOM_MAX),
    portee: z.enum(NOTES_PORTEES).default("perso"),
    /** `et` : toutes les regles ; `ou` : au moins une. */
    combinaison: z.enum(["et", "ou"]).default("et"),
    regles: z.array(NoteRegleSchema).max(NOTE_COLLECTION_REGLES_MAX).default([]),
    /** Les notes ajoutees a la main (toujours dedans, regles ou non). */
    selection: z.array(z.string().min(1)).max(NOTE_COLLECTION_SELECTION_MAX).default([]),
  })
  .strict();
export type NoteCollectionSaisie = z.input<typeof NoteCollectionSaisieSchema>;

export interface NoteCollection {
  id: string;
  orgId: string;
  nom: string;
  portee: NotePortee;
  auteurUid: string;
  combinaison: "et" | "ou";
  regles: NoteRegle[];
  selection: string[];
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  updatedBy: string;
}

const JOUR_MS = 86_400_000;

/** Une regle vaut-elle pour cette note, vue par `uid`, a l'instant `maintenant` ? */
export function regleVaut(note: Pick<Note, "tags" | "titre" | "favorisDe" | "journal" | "portee" | "createdAt" | "modifieeAt">, regle: NoteRegle, uid: string, maintenant: Date = new Date()): boolean {
  switch (regle.champ) {
    case "tag":
      return regle.op === "contient" ? note.tags.includes(regle.valeur) : !note.tags.includes(regle.valeur);
    case "titre": {
      const dedans = motsNormalises(note.titre).join(" ").includes(motsNormalises(regle.valeur).join(" "));
      return regle.op === "contient" ? dedans : !dedans;
    }
    case "favori":
      return note.favorisDe.includes(uid) === regle.valeur;
    case "journal":
      return (note.journal !== null) === regle.valeur;
    case "portee":
      return note.portee === regle.valeur;
    case "creee":
    case "modifiee": {
      const date = regle.champ === "creee" ? note.createdAt : note.modifieeAt;
      return maintenant.getTime() - date.getTime() <= regle.valeur * JOUR_MS;
    }
    default:
      return false;
  }
}

/**
 * LA NOTE EST-ELLE DANS LA COLLECTION ? Selection d'abord (toujours dedans),
 * puis les regles. Une collection sans regle ne contient que sa selection.
 */
export function noteDansCollection(
  note: Pick<Note, "id" | "tags" | "titre" | "favorisDe" | "journal" | "portee" | "createdAt" | "modifieeAt">,
  collection: Pick<NoteCollection, "combinaison" | "regles" | "selection">,
  uid: string,
  maintenant: Date = new Date(),
): boolean {
  if (collection.selection.includes(note.id)) return true;
  if (collection.regles.length === 0) return false;
  const verdicts = collection.regles.map((regle) => regleVaut(note, regle, uid, maintenant));
  return collection.combinaison === "et" ? verdicts.every(Boolean) : verdicts.some(Boolean);
}

/* -------------------------------------------------------------------------- */
/* Saisies de la callable `notesApi` et des actions MCP                       */
/* -------------------------------------------------------------------------- */

export const NoteCreationSchema = z
  .object({
    portee: z.enum(NOTES_PORTEES).default("perso"),
    titre: z.string().max(NOTE_TITRE_MAX).default(""),
    mode: z.enum(NOTES_MODES).default("page"),
    tags: z.array(z.string().min(1)).max(NOTE_TAGS_MAX).default([]),
    /** Le contenu de depart, en Markdown (le MCP, Lia, l'import). */
    markdown: z.string().max(NOTE_MARKDOWN_MAX).optional(),
  })
  .strict();
export type NoteCreation = z.input<typeof NoteCreationSchema>;

/** Ce que l'editeur renvoie au serveur apres une modification (titre, texte, liens), debounce. */
export const NoteSynchroSchema = z
  .object({
    titre: z.string().max(NOTE_TITRE_MAX),
    /** Le texte brut de la note (coupe par le client a `NOTE_TEXTE_SYNCHRO_MAX`). */
    texte: z.string().max(20_000),
    liens: z.array(z.string().min(1)).max(NOTE_LIENS_MAX),
    mode: z.enum(NOTES_MODES).optional(),
    /**
     * Les membres mentionnes (`personId`, une entree par pastille, dans
     * l'ordre du document). Une DECLARATION du client : le serveur la relit
     * dans le contenu avant de notifier qui que ce soit. Absent : un editeur
     * d'avant les mentions, rien ne change.
     */
    mentions: z.array(z.string().regex(/^[A-Za-z0-9_-]{1,64}$/)).max(NOTE_MENTIONS_MAX).optional(),
  })
  .strict();
export type NoteSynchro = z.infer<typeof NoteSynchroSchema>;
export const NOTE_TEXTE_SYNCHRO_MAX = 20_000;

export const NoteTagSaisieSchema = z
  .object({
    nom: z.string().trim().min(1).max(NOTE_TAG_NOM_MAX),
    couleur: z.enum(NOTES_COULEURS_TAG).default("gris"),
  })
  .strict();
export type NoteTagSaisie = z.input<typeof NoteTagSaisieSchema>;

/* -------------------------------------------------------------------------- */
/* Fonctions pures partagees (client, serveur, MCP)                           */
/* -------------------------------------------------------------------------- */

/**
 * LES DEBUTS DE MOTS d'une note (titre puis texte), pour une recherche
 * serveur par `array-contains` : « Réunion lundi » -> r, re, reu... Le titre
 * passe d'abord : un texte long ne chasse jamais un mot du titre.
 */
export function prefixesDeNote(titre: string, texte: string): string[] {
  const prefixes: string[] = [];
  const vus = new Set<string>();
  for (const mot of [...motsNormalises(titre), ...motsNormalises(texte)]) {
    const borne = Math.min(mot.length, NOTE_RECHERCHE_PREFIXE_MAX);
    for (let longueur = 1; longueur <= borne; longueur += 1) {
      const prefixe = mot.slice(0, longueur);
      if (vus.has(prefixe)) continue;
      if (prefixes.length >= NOTE_RECHERCHE_MAX) return prefixes;
      vus.add(prefixe);
      prefixes.push(prefixe);
    }
  }
  return prefixes;
}

/** Le terme a chercher dans `recherche` : le premier mot normalise, coupe au plafond, ou `null`. */
export function termeDeRecherche(saisie: string): string | null {
  const mot = motsNormalises(saisie)[0];
  return mot === undefined ? null : mot.slice(0, NOTE_RECHERCHE_PREFIXE_MAX);
}

/** Une note correspond-elle a TOUS les mots saisis (titre et extrait), cote client ? */
export function noteCorrespond(note: Pick<Note, "titre" | "extrait" | "recherche">, saisie: string): boolean {
  const mots = motsNormalises(saisie);
  if (mots.length === 0) return true;
  const prefixes = new Set(note.recherche);
  const texte = motsNormalises(`${note.titre} ${note.extrait}`).join(" ");
  return mots.every((mot) => prefixes.has(mot.slice(0, NOTE_RECHERCHE_PREFIXE_MAX)) || texte.includes(mot));
}

/** L'extrait d'un texte : espaces resserres, coupe au mot. */
export function extraitDe(texte: string, max = NOTE_EXTRAIT_MAX): string {
  const net = texte.replace(/\s+/g, " ").trim();
  if (net.length <= max) return net;
  const coupe = net.slice(0, max);
  const espace = coupe.lastIndexOf(" ");
  return `${(espace > max * 0.6 ? coupe.slice(0, espace) : coupe).trimEnd()}…`;
}

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

/** Le titre d'une note de journal : « 13 octobre 2026 ». */
export function titreDeJournal(date: string): string {
  if (!estDatePure(date)) throw new Error(`Date de journal invalide : ${date}`);
  const [annee, mois, jour] = date.split("-").map(Number) as [number, number, number];
  return `${jour} ${MOIS[mois - 1]} ${annee}`;
}

/** Le titre affiche d'une note : son titre, sinon « Sans titre ». */
export function titreAffiche(note: Pick<Note, "titre">): string {
  return note.titre.trim() === "" ? "Sans titre" : note.titre;
}

/** Qui peut MODIFIER une note (l'ecran grise, le serveur et les rules tranchent). */
export function peutModifierNote(note: Pick<Note, "portee" | "auteurUid" | "archivedAt">, moi: { uid: string; role: string }): boolean {
  if (moi.role === "viewer") return false;
  if (note.auteurUid === moi.uid) return true;
  return note.portee === "org" && (moi.role === "owner" || moi.role === "admin");
}

/** Qui peut LIRE une note. */
export function peutLireNote(note: Pick<Note, "portee" | "auteurUid">, moi: { uid: string }): boolean {
  return note.portee === "org" || note.auteurUid === moi.uid;
}

/** Les liens retour d'une note : les notes visibles qui la citent. */
export function liensRetour<T extends Pick<Note, "id" | "liens" | "deletedAt">>(noteId: string, notes: readonly T[]): T[] {
  return notes.filter((n) => n.id !== noteId && n.deletedAt === null && n.liens.includes(noteId));
}

/** L'adresse d'une note dans l'app (lien profond, notifications, MCP). */
export function cheminDeNote(noteId: string, vue: "org" | "perso" | "toutes" = "toutes"): string {
  return `/app/${NOTES_APP_ID}?s=${vue}&id=${encodeURIComponent(noteId)}`;
}
